import { useMutation } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

import { authAPI } from '@/api/new-api'
import { toastFactory } from '@/factories'
import { downloadHref, getApiErrorMessage } from '@/utils'

export interface ExportMarketShopTransactionsRequest {
  buildingId: number
  from?: number
  to?: number
}

export interface DownloadTransactionsRequest extends ExportMarketShopTransactionsRequest {
  fileName: string
}

export const exportMarketShopTransactions = async (data: ExportMarketShopTransactionsRequest) => {
  const response = await authAPI.get<Blob>('/market-shop/export-transactions', {
    params: {
      buildingId: data.buildingId,
      from: data.from,
      to: data.to,
    },
    responseType: 'blob',
  })
  return response.data
}

export const useExportMarketShopTransactions = () => {
  const objectUrlRef = useRef<string | null>(null)

  const mutation = useMutation({
    mutationFn: async ({ fileName, ...rest }: DownloadTransactionsRequest) => {
      const blob = await exportMarketShopTransactions(rest)
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current)
      objectUrlRef.current = URL.createObjectURL(blob)
      downloadHref(objectUrlRef.current, fileName)
      return objectUrlRef.current
    },
    onError: (error) => {
      toastFactory.error(getApiErrorMessage(error))
    },
  })

  // Note: Clean up the URL object when the hook unmounts
  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current)
    }
  }, [])

  return mutation
}
